/**
 * Client do back (riodg-back).
 *
 * O front é servido de origem diferente da API (`app.*` × `api.*`), então toda
 * chamada é cross-origin: a base vem de configuração e as credenciais viajam
 * por cookie — daí o `credentials: 'include'`, que exige a origem estar na
 * lista de CORS do back.
 *
 * Autorização é decidida no back. O front esconde botão, não protege dado.
 *
 * A chave compartilhada provisória do acervo saiu com o P-03: a sessão é o
 * cookie, e quem autoriza é o servidor a cada requisição.
 */

// Vazio = mesma origem, passando pelo proxy do dev server (ou pelo nginx em
// produção). É o que permite expor o painel por um túnel só, sem CORS.
const BASE = import.meta.env.VITE_API_URL ?? ''

export class ErroApi extends Error {
  status: number
  corpo: unknown

  constructor(status: number, corpo: unknown) {
    super(`API respondeu ${status}`)
    this.status = status
    this.corpo = corpo
  }
}

async function requisitar<T>(caminho: string, init?: RequestInit): Promise<T> {
  const resposta = await fetch(`${BASE}${caminho}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  })

  const texto = await resposta.text()
  const corpo = texto ? JSON.parse(texto) : null

  if (!resposta.ok) throw new ErroApi(resposta.status, corpo)

  return corpo as T
}

/**
 * Envio de arquivo.
 *
 * 🔴 Separado do `requisitar` porque **não pode levar `Content-Type`**: com
 * `FormData`, quem monta o cabeçalho — com a fronteira do multipart — é o
 * navegador. Defini-lo à mão produz um corpo que o servidor lê como vazio, e o
 * erro só aparece do outro lado.
 */
async function enviarFormulario<T>(caminho: string, corpo: FormData): Promise<T> {
  const resposta = await fetch(`${BASE}${caminho}`, {
    method: 'POST',
    credentials: 'include',
    body: corpo,
  })

  const texto = await resposta.text()
  const conteudo = texto ? JSON.parse(texto) : null

  if (!resposta.ok) throw new ErroApi(resposta.status, conteudo)

  return conteudo as T
}

export const api = {
  enviarFormulario,
  get: <T>(caminho: string) => requisitar<T>(caminho),
  post: <T>(caminho: string, corpo: unknown) =>
    requisitar<T>(caminho, { method: 'POST', body: JSON.stringify(corpo) }),
  put: <T>(caminho: string, corpo: unknown) =>
    requisitar<T>(caminho, { method: 'PUT', body: JSON.stringify(corpo) }),
  delete: <T>(caminho: string) => requisitar<T>(caminho, { method: 'DELETE' }),
}

export const saude = () => api.get<{ status: string; em: string }>('/saude')
